/**
 * Imports required for the Drawer component
 */
import { PropsWithChildren, Ref } from 'react';
import { DrawerProps } from './props';
import styles from './styles.module.scss';
import { AnimatePresence, motion } from 'framer-motion';
import { containerVariants, overlayVariant } from './variants';
import { useClickOutside } from '@starsoft/common/hooks';

/**
 * Drawer Component
 * A sliding drawer component with animation and click outside handling
 *
 * @param {Object} props - The component props
 * @param {Function} props.onClose - Function called when drawer should close
 * @param {boolean} props.open - Whether the drawer is open
 * @param {ReactNode} props.children - Child elements to render inside drawer
 * @param {boolean} props.isDisabled - Whether the drawer is disabled
 * @param {boolean} props.absolute - Whether to use absolute positioning
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element | null} Rendered drawer component or null if closed/disabled
 */
export function Drawer({
  onClose,
  open,
  children,
  isDisabled,
  absolute,
  className = '',
}: PropsWithChildren<DrawerProps>) {
  // Hook to handle clicks outside the drawer
  const ref = useClickOutside({
    open,
    onClickOutside: onClose,
  });

  return (
    <AnimatePresence>
      {open && !isDisabled && (
        <>
          <motion.div
            className={`${styles.overlay} ${absolute ? styles['overlay--absolute'] : ''}`}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={overlayVariant}
          />

          <motion.div
            className={`${styles.container} ${absolute ? styles['container--absolute'] : ''} ${className ? className : ''}`}
            ref={ref as Ref<HTMLDivElement>}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
